import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Copy, KeyRound } from "lucide-react";
import { createUser } from "../../api/admin.js";
import type { AccountRole } from "../../api/auth.js";
import { ApiClientError } from "../../api/http.js";
import { generateTempPassword, tempCredentials } from "../../app/authFlow.js";
import { useSecretCopy } from "../../app/clipboard.js";
import { isDesktop } from "../../app/platform.js";
import { useSession } from "../../app/session.js";
import { formatDateTime } from "../../lib/format.js";
import { isValidEmail } from "../../lib/masterPassword.js";
import { Dialog } from "../../ui/Dialog.js";
import { Field, SelectInput, TextInput } from "../../ui/forms.js";
import { Alert, Button } from "../../ui/primitives.js";

export interface IssuedCredential {
  readonly email: string;
  readonly password: string;
  readonly expiresAt: number | null;
}

/** Shows a one-time password exactly once. It is not stored anywhere; closing the dialog drops it. */
export function CredentialView(props: { readonly credential: IssuedCredential; readonly onDone: () => void }): ReactNode {
  const copy = useSecretCopy();
  const origin = isDesktop() ? undefined : window.location.origin;
  const expiry = props.credential.expiresAt === null ? undefined : formatDateTime(props.credential.expiresAt);
  const handout = [
    origin !== undefined ? `Anmeldung: ${origin}` : undefined,
    `E-Mail: ${props.credential.email}`,
    `Einmalpasswort: ${props.credential.password}`,
    expiry !== undefined ? `Gültig bis: ${expiry}` : undefined,
    "Bei der Anmeldung legst du dein eigenes Master-Passwort fest."
  ].filter((line: string | undefined): line is string => line !== undefined).join("\n");

  return (
    <div className="flex flex-col gap-5 p-5">
      <Alert tone="info">
        Das Einmalpasswort wird nur jetzt angezeigt und nirgends gespeichert. Gib es auf einem sicheren Weg weiter, getrennt von der E-Mail-Adresse.
      </Alert>
      <dl className="grid gap-3 rounded-lg border border-line bg-ink p-4 text-sm">
        <div>
          <dt className="text-xs text-mist-2">E-Mail</dt>
          <dd className="mt-0.5 break-all text-foam">{props.credential.email}</dd>
        </div>
        <div>
          <dt className="text-xs text-mist-2">Einmalpasswort</dt>
          <dd className="mt-0.5 break-all font-mono text-base tracking-wider text-foam">{props.credential.password}</dd>
        </div>
        {expiry !== undefined ? (
          <div>
            <dt className="text-xs text-mist-2">Gültig bis</dt>
            <dd className="mt-0.5 text-foam">{expiry}</dd>
          </div>
        ) : null}
      </dl>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" icon={Copy} onClick={(): void => void copy(props.credential.password, "Einmalpasswort kopiert")}>
          Passwort kopieren
        </Button>
        <Button variant="secondary" icon={Copy} onClick={(): void => void copy(handout, "Zugangsdaten kopiert")}>
          Alles kopieren
        </Button>
        <Button onClick={props.onDone}>Fertig</Button>
      </div>
    </div>
  );
}

export function CreateUserDialog(props: { readonly onClose: () => void; readonly onCreated: () => void }): ReactNode {
  const { state } = useSession();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AccountRole>("user");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [issued, setIssued] = useState<IssuedCredential | undefined>(undefined);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || state.status !== "unlocked") {
      return;
    }
    if (!isValidEmail(email)) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    const address = email.trim().toLowerCase();
    setBusy(true);
    setError(undefined);
    try {
      const password = generateTempPassword();
      const proof = await tempCredentials(state.client, address, password);
      const user = await createUser({ email: address, role, ...proof });
      setIssued({ email: user.email, password, expiresAt: user.tempPasswordExpiresAt });
      props.onCreated();
    } catch (err: unknown) {
      setError(err instanceof ApiClientError ? err.message : "Das Konto konnte nicht angelegt werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={(): void => {
        if (!busy) {
          props.onClose();
        }
      }}
      dismissible={!busy && issued === undefined}
      title={issued === undefined ? "Konto anlegen" : "Zugangsdaten"}
      description={issued === undefined ? "Die Person meldet sich mit einem Einmalpasswort an und legt dann ihr eigenes Master-Passwort fest." : undefined}
      variant="center"
      size="sm"
    >
      {issued !== undefined ? (
        <CredentialView credential={issued} onDone={props.onClose} />
      ) : (
        <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5 p-5" noValidate>
          <Field id="new-user-email" label="E-Mail">
            <TextInput
              id="new-user-email"
              type="email"
              inputMode="email"
              autoComplete="off"
              autoFocus
              value={email}
              placeholder="name@firma.de"
              disabled={busy}
              onChange={(event): void => setEmail(event.target.value)}
            />
          </Field>
          <Field id="new-user-role" label="Rolle" hint={role === "admin" ? "Administratoren können Konten anlegen, sperren und löschen." : "Normale Konten sehen nur ihren eigenen Tresor und ihre Teams."}>
            <SelectInput id="new-user-role" value={role} disabled={busy} onChange={(event): void => setRole(event.target.value === "admin" ? "admin" : "user")}>
              <option value="user">Benutzer</option>
              <option value="admin">Administrator</option>
            </SelectInput>
          </Field>
          {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={props.onClose} disabled={busy}>
              Abbrechen
            </Button>
            <Button type="submit" icon={KeyRound} loading={busy}>
              Anlegen & Einmalpasswort erzeugen
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
